import get from 'lodash/get';
import {
	REQUEST_TYPE,
	ISSUE_TYPE,
	UNASSIGNED_REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type {
	Project,
	IssueType,
	RequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformStargateUrl } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-stargate-url/index.tsx';
import type {
	ProjectResponse,
	ProjectEntity,
	ProjectField,
	IssueTypeEntity,
	IssueTypeResponse,
	IssueTypeField,
	RequestTypeEntity,
	RequestTypeResponse,
	RequestTypeField,
} from '../../types/context-fields.tsx';

const transformProjectEntity = ({
	id,
	name,
	avatar,
	projectType,
	simplified,
	key,
	classificationTags = [],
}: ProjectEntity): Project => ({
	label: `${name} (${key})`,
	value: id,
	iconUrl: avatar.large,
	// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ProjectType'.
	projectType: projectType && projectType.toLowerCase(),
	projectKey: key,
	isSimplified: simplified,
	classificationTags,
});

const transformProjects = (projects: ProjectEntity[]): Project[] =>
	projects.map((project: ProjectEntity) => transformProjectEntity(project));

const transformProjectField = (projectResponse: ProjectResponse): ProjectField => {
	const { project, ...rest } = projectResponse;
	return {
		...rest,
		selectedProject: project ? transformProjectEntity(project) : null,
	};
};

const transformIssueTypeEntity = ({ id, name, avatar }: IssueTypeEntity): IssueType => ({
	label: name,
	value: id,
	iconUrl: avatar.large,
	type: ISSUE_TYPE,
});

const transformIssueTypes = (issueTypes: IssueTypeEntity[]): IssueType[] =>
	issueTypes.map((issueType) => transformIssueTypeEntity(issueType));

const transformIssueTypeField = (issueTypeResponse: IssueTypeResponse): IssueTypeField => {
	const { issueType, ...rest } = issueTypeResponse;
	const issueTypes = get(issueTypeResponse, 'issueTypes.nodes', []);
	return {
		...rest,
		selectedIssueType: issueType ? transformIssueTypeEntity(issueType) : null,
		issueTypes: transformIssueTypes(issueTypes),
	};
};

const transformRequestTypeEntity = (
	{
		requestTypeId,
		name,
		description,
		helpText,
		key,
		avatar,
		issueType: { issueTypeId, issueTypeName },
		practices,
	}: RequestTypeEntity,
	hasPractice: boolean,
): RequestType => ({
	label: name,
	description: description || '',
	helpText: helpText || '',
	value: `${issueTypeId}-${requestTypeId}`,
	iconUrl: transformStargateUrl(avatar.large || ''),
	requestTypeKey: key,
	issueTypeId,
	issueTypeName,
	practice: get(practices, '0.key', hasPractice ? UNASSIGNED_REQUEST_TYPE : REQUEST_TYPE),
	type: REQUEST_TYPE,
});

const transformRequestTypes = (requestTypes: RequestTypeEntity[]): RequestType[] => {
	const hasPractice = requestTypes.some((rt) => rt.practices.length > 0);
	// sort request types alphabetically
	return requestTypes
		.map((requestType) => transformRequestTypeEntity(requestType, hasPractice))
		.sort((a, b) => a.label.localeCompare(b.label));
};

const transformRequestTypeField = (requestTypeResponse: RequestTypeResponse): RequestTypeField => {
	const { requestType, ...rest } = requestTypeResponse;
	const requestTypes = get(requestTypeResponse, 'requestTypes.nodes', []);
	const hasPractice = requestTypes.some((rt) => rt.practices.length > 0);

	return {
		...rest,
		selectedRequestType: requestType ? transformRequestTypeEntity(requestType, hasPractice) : null,
		requestTypes: transformRequestTypes(requestTypes),
	};
};

export {
	transformProjectEntity,
	transformProjects,
	transformProjectField,
	transformIssueTypeEntity,
	transformIssueTypes,
	transformIssueTypeField,
	transformRequestTypeEntity,
	transformRequestTypes,
	transformRequestTypeField,
};
